/**
 * Деньги на экране и деньги в поле ввода — единственное место, где минорные единицы
 * превращаются в текст и обратно.
 *
 * ## Почему не `Intl.NumberFormat(locale, {style: 'currency'})`
 *
 * Две причины, и обе уже стоили этому приложению кода в `shared/lib/format.ts`.
 *
 * Первая — ICU. Hermes собирают и без полных данных ICU; тогда `toLocaleString` молча
 * отдаёт американский порядок, и «500 000,00» приезжает как «500,000.00». Для даты это
 * заметно сразу, для суммы — нет: цифры те же, разделители переставлены, и читатель
 * увидит правдоподобную неправду.
 *
 * Вторая — валюта. `style: 'currency'` рисует символ («сум», «$»), а ведомость бывает
 * в нескольких валютах сразу, и там нужен именно КОД: `UZS` и `USD` рядом не спутать,
 * а «сум» и «$» в соседних строках читаются как оформление, а не как разные деньги.
 * Ровно по этой же причине серверная выгрузка держит валюту отдельной колонкой.
 *
 * ## Экспонента
 *
 * Всегда две цифры после запятой. Сервер описывает минорную единицу именно так
 * («для UZS это тийины, две цифры после запятой») и считает по этому же правилу; свой
 * справочник экспонент по валютам (JPY — ноль, KWD — три) означал бы, что клиент
 * ставит запятую не там, где её поставил сервер. Если у API однажды появится валюта
 * с другой экспонентой, чинить это надо здесь и вместе с сервером.
 */

/** Сколько цифр после запятой в минорной единице. См. модульный комментарий. */
export const MINOR_DIGITS = 2;

const MINOR_FACTOR = 10 ** MINOR_DIGITS;

/**
 * Разделитель разрядов — УЗКИЙ НЕРАЗРЫВНЫЙ ПРОБЕЛ (U+202F), как в `formatCount`:
 * обычный пробел позволяет числу переноситься по строке посередине, а запятая читается
 * русским и узбекским читателем как десятичный знак.
 */
const GROUP = ' ';

/** Десятичный знак. Запятая — русская и узбекская норма, и та же, что в отчётах. */
const DECIMAL = ',';

/**
 * Между суммой и кодом валюты — НЕРАЗРЫВНЫЙ пробел (U+00A0). Обычный позволяет строке
 * «500 000,00 UZS» разорваться на «500 000,00» и «UZS» в разных строках таблицы, и
 * тогда в колонке, где валюты две, сумма и её валюта перестают быть одним объектом.
 */
const CURRENCY_GAP = '\u00a0';

function group(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP);
}

/**
 * Минорные единицы → «500 000,00». Без кода валюты: он приезжает отдельно, потому что
 * в ведомости колонка суммы и колонка валюты — разные колонки.
 */
export function formatMinor(minor: number): string {
  if (!Number.isFinite(minor)) {
    return '—';
  }
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minor));
  const whole = Math.floor(abs / MINOR_FACTOR);
  const frac = abs % MINOR_FACTOR;
  return `${sign}${group(String(whole))}${DECIMAL}${String(frac).padStart(MINOR_DIGITS, '0')}`;
}

/**
 * Сумма с валютой — «500 000,00 UZS».
 *
 * `null` отдаёт длинное тире, а не ноль: в ведомости пусто и ноль — разные факты
 * (см. `PayrollRow.accrued_minor`), и превращать одно в другое здесь было бы тем же
 * враньём, только в форматтере.
 */
export function formatMoney(
  minor: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (minor == null) {
    return '—';
  }
  const code = (currency ?? '').trim().toUpperCase();
  return code ? `${formatMinor(minor)}${CURRENCY_GAP}${code}` : formatMinor(minor);
}

/**
 * Что человек напечатал → минорные единицы, либо `null`, если это не сумма.
 *
 * Принимает то, что реально набирают: пробелы любого рода как разделитель разрядов,
 * запятую и точку как десятичный знак. Не принимает больше двух знаков после
 * запятой — молча округлить введённое значит взять на себя решение о чужих деньгах,
 * и форма должна сказать об этом вслух.
 *
 * Отрицательное не проходит: сервер отвечает `422`, и ловить это на форме дешевле.
 */
export function parseMoneyToMinor(text: string): number | null {
  // `\s` уже покрывает и NBSP, и узкий неразрывный пробел, которым разделяет разряды
  // сам этот модуль: вставленную обратно в поле сумму надо принять как есть.
  const cleaned = text.replace(/[\s']/g, '').replace(',', '.');
  if (cleaned === '' || !/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return null;
  }
  const [whole, frac = ''] = cleaned.split('.');
  const minor =
    Number(whole) * MINOR_FACTOR + Number(frac.padEnd(MINOR_DIGITS, '0'));
  return Number.isSafeInteger(minor) ? minor : null;
}

/** Минорные единицы → то, что кладут обратно в поле ввода: «500000.00». */
export function minorToInput(minor: number): string {
  const abs = Math.abs(Math.round(minor));
  const whole = Math.floor(abs / MINOR_FACTOR);
  const frac = abs % MINOR_FACTOR;
  return frac === 0
    ? String(whole)
    : `${whole}.${String(frac).padStart(MINOR_DIGITS, '0')}`;
}

/** Код валюты в каноническом виде, либо null — три латинские буквы, и только они. */
export function normalizeCurrency(text: string): string | null {
  const code = text.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
}

/** Валюта по умолчанию — та же, что подставляет сервер, если её не прислать. */
export const DEFAULT_CURRENCY = 'UZS';
